/**
 * Per-file development logger utility
 *
 * Creates a logger instance that only outputs verbose debug/info logs in development
 * when explicitly enabled for that module. Warnings and errors are ALWAYS logged
 * regardless of environment or enabled state.
 *
 * In dev mode, `enabled` defaults to `true` (all modules log verbosely).
 * Set `enabled: false` on noisy modules to silence their debug/info output.
 *
 * In dev mode, log entries are automatically batched and shipped to the
 * dev-logger server (port 3099) which persists them as JSONL files in
 * `/logs/{project}/latest.jsonl`. The project name is auto-detected from:
 *   - Server-side: the `DEV_LOG_PROJECT` env var set in each app's `nuxt.config.ts`
 *   - Client-side: the `__DEV_LOG_PROJECT__` build-time constant injected by Vite
 *     via `define` in each app's `nuxt.config.ts`. This makes client-side log
 *     shipping work in browser/WebView contexts where `process.env` is not available.
 *
 * @example Server-side usage
 * ```typescript
 * import { createLogger } from '#bubba/shared/utils/logger';
 *
 * const log = createLogger('MyModule');
 * log.debug('Detailed info', { data }); // Only in dev when enabled
 * log.info('Operation started');         // Only in dev when enabled
 * log.warn('Deprecated feature used');   // Always logged
 * log.error('Failed to process', error); // Always logged
 * ```
 *
 * @example Client-side usage — no special setup needed, project is auto-detected
 * ```typescript
 * import { createLogger } from '#bubba/shared/utils/logger';
 *
 * const log = createLogger('MyComponent');
 * log.debug('Detail', { data }); // shipped to log server in dev
 * ```
 *
 * @example Temporarily disable verbose logging for a noisy module
 * ```typescript
 * const log = createLogger('NoisyModule', { enabled: false });
 * // debug/info calls will be silenced, warn/error still work
 * ```
 *
 * @example Enable all loggers globally (override per-module settings)
 * Set environment variable: DEBUG_ALL=true
 */

// Vite replaces this token at build time with the project name string (or `undefined`
// in production builds). Declaring it here keeps TypeScript happy without a separate
// global.d.ts. The `typeof` guard makes it safe when the token is not defined (e.g.
// in Node/Convex environments that don't go through Vite).
declare const __DEV_LOG_PROJECT__: string | undefined;

/** Configuration options for creating a logger instance */
export interface LoggerOptions {
  /**
   * Enable verbose logging (debug/info) for this module.
   * Defaults to `true` in development, `false` in production.
   * Set explicitly to `false` to silence noisy modules in dev.
   */
  enabled?: boolean;

  /**
   * Override development environment detection.
   * The logger auto-detects this from NODE_ENV (server-side) and the
   * `__DEV_LOG_PROJECT__` Vite define (client-side), so you rarely need
   * to pass this explicitly.
   * @default auto-detected from NODE_ENV / import.meta.env.DEV
   */
  isDev?: boolean;
}

/** Logger instance with leveled logging methods */
export interface Logger {
  /** Verbose debug output - only in dev when enabled */
  debug: (...args: unknown[]) => void;
  /** Informational output - only in dev when enabled */
  info: (...args: unknown[]) => void;
  /** Warning output - ALWAYS logged regardless of settings */
  warn: (...args: unknown[]) => void;
  /** Error output - ALWAYS logged regardless of settings */
  error: (...args: unknown[]) => void;
}

/**
 * Check if we're in a development environment.
 * Works at build-time via process.env (server/Node), and via import.meta.env (browser/Vite).
 */
function isDevEnvironment(): boolean {
  // Check for global debug override first
  if (typeof process !== 'undefined' && process.env?.DEBUG_ALL === 'true') {
    return true;
  }
  // Check ENV variable (set in nuxt.config.ts)
  if (typeof process !== 'undefined' && process.env?.ENV === 'development') {
    return true;
  }
  // Fallback to NODE_ENV for cases where ENV isn't set (server-side Node.js / Nuxt SSR)
  if (
    typeof process !== 'undefined' &&
    process.env?.NODE_ENV === 'development'
  ) {
    return true;
  }
  // Browser/Vite: import.meta.env.DEV is true in dev mode, false in prod
  // This is inlined at build time by Vite so it works in the browser.
  try {
    if ((import.meta as any).env?.DEV === true) return true;
  } catch {
    // import.meta.env may not exist in all environments (e.g. Convex backend)
  }
  return false;
}

/**
 * Check if global debug override is enabled.
 * When DEBUG_ALL=true, all loggers output verbose logs regardless of their enabled flag.
 */
function isGlobalDebugEnabled(): boolean {
  if (typeof process !== 'undefined' && process.env?.DEBUG_ALL === 'true') {
    return true;
  }
  return false;
}

/** No-op function for disabled log methods */
const noop = (): void => {};

// ---------------------------------------------------------------------------
// Dev log shipping — batched fire-and-forget to the dev-logger server
// ---------------------------------------------------------------------------

const DEV_LOGGER_URL = 'http://127.0.0.1:3099/logs';
const FLUSH_INTERVAL_MS = 200;

interface DevLogEntry {
  ts: string;
  module: string;
  level: string;
  args: unknown[];
}

/** Per-project buffer of log entries waiting to be flushed. */
const logBuffer = new Map<string, DevLogEntry[]>();

/** Whether the flush timer has been started. */
let flushTimerStarted = false;

/** Serialize a single argument for JSON transport. */
function serializeArg(arg: unknown): unknown {
  if (arg instanceof Error) {
    return { __error: true, name: arg.name, message: arg.message, stack: arg.stack };
  }
  if (typeof arg === 'function') {
    return `[Function: ${arg.name || 'anonymous'}]`;
  }
  if (typeof arg === 'bigint') {
    return arg.toString();
  }
  if (typeof arg === 'symbol') {
    return arg.toString();
  }
  return arg;
}

/** Push a log entry into the buffer for a given project. */
function bufferLogEntry(project: string, module: string, level: string, args: unknown[]): void {
  let entries = logBuffer.get(project);
  if (!entries) {
    entries = [];
    logBuffer.set(project, entries);
  }
  entries.push({
    ts: new Date().toISOString(),
    module,
    level,
    args: args.map(serializeArg),
  });
  ensureFlushTimer();
}

/** Flush all buffered log entries to the dev-logger server. */
function flushLogBuffer(): void {
  for (const [project, entries] of logBuffer) {
    if (entries.length === 0) continue;
    const batch = entries.splice(0);
    try {
      fetch(DEV_LOGGER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project, batch }),
      }).catch(noop); // fire-and-forget — server may not be running
    } catch {
      // fetch itself may throw synchronously in some runtimes
    }
  }
}

/** Start the periodic flush timer if not already running. */
function ensureFlushTimer(): void {
  if (flushTimerStarted) return;
  flushTimerStarted = true;

  if (typeof setInterval !== 'undefined') {
    const timer = setInterval(flushLogBuffer, FLUSH_INTERVAL_MS);
    // unref so the timer doesn't prevent the process from exiting
    if (typeof timer === 'object' && 'unref' in timer) timer.unref();
  }
}

// ---------------------------------------------------------------------------
// Logger factory
// ---------------------------------------------------------------------------

/** Auto-detect project name from env var (server) or Vite define (browser). */
function detectProject(): string | undefined {
  // Server-side (Node.js / Nuxt SSR / Convex): read from process.env
  if (typeof process !== 'undefined') {
    const envProject = process.env?.DEV_LOG_PROJECT;
    if (envProject) return envProject;
  }
  // Client-side (browser/WebView): read from the Vite build-time constant.
  // Each app's nuxt.config.ts injects `define: { __DEV_LOG_PROJECT__: '"client"' }`.
  // In production builds the define is set to `undefined`, so this is a noop there.
  try {
    if (typeof __DEV_LOG_PROJECT__ !== 'undefined' && __DEV_LOG_PROJECT__) {
      return __DEV_LOG_PROJECT__;
    }
  } catch {
    // Token not defined in this environment (e.g. Convex backend) — ignore
  }
  return undefined;
}

/**
 * Creates a logger instance for a specific module/file.
 *
 * Verbose logs (debug/info) are only output when:
 * 1. Environment is development (ENV=development or NODE_ENV=development)
 * 2. AND the logger is enabled (defaults to true in dev) OR DEBUG_ALL=true
 *
 * Warnings and errors are ALWAYS logged regardless of environment or enabled state.
 *
 * @param moduleName - Name to prefix log messages with (e.g., 'MyComponent', 'AuthService')
 * @param options - Configuration options
 * @returns Logger instance with debug, info, warn, error methods
 */
export function createLogger(
  moduleName: string,
  options: LoggerOptions = {},
): Logger {
  const isDev = options.isDev ?? isDevEnvironment();
  // In dev, default to enabled=true so all modules log verbosely.
  // Modules can still pass enabled: false to opt out.
  const enabled = options.enabled ?? isDev;

  const prefix = `[${moduleName}]`;
  const project = detectProject();
  const shouldShipLogs = isDev && !!project;

  // Determine if verbose logging should be active
  const isVerboseActive = isDev && (enabled || isGlobalDebugEnabled());

  return {
    debug: isVerboseActive
      ? (...args: unknown[]) => {
        console.debug(prefix, ...args);
        if (shouldShipLogs) bufferLogEntry(project, moduleName, 'debug', args);
      }
      : noop,

    info: isVerboseActive
      ? (...args: unknown[]) => {
        console.info(prefix, ...args);
        if (shouldShipLogs) bufferLogEntry(project, moduleName, 'info', args);
      }
      : noop,

    // warn and error are ALWAYS logged - these indicate real issues
    warn: (...args: unknown[]) => {
      console.warn(prefix, ...args);
      if (shouldShipLogs) bufferLogEntry(project, moduleName, 'warn', args);
    },

    error: (...args: unknown[]) => {
      console.error(prefix, ...args);
      if (shouldShipLogs) bufferLogEntry(project, moduleName, 'error', args);
    },
  };
}
